import { ConfigService } from '@axo/config';
import { Product } from '@axo/shared/data-access/types';
import { TranslationProvider } from '@axo/shared/i18n';
import { MarketCountryCode } from '@axo/shared/types';
import { Locales } from 'libs/shared/i18n/src/lib/types';
import { ComponentProps, PropsWithChildren, useRef } from 'react';
import { AuthenticationContextProvider } from '../context/Authentication/AuthenticationContext';
import { AxoFormContextProvider } from '../context/AxoForm/AxoForm';
import { DataAccessErrorFeedbackProvider } from '../context/DataAccessErrorFeedback';

type FormGlobalProvidersProps<Data extends Record<string, unknown>> =
  PropsWithChildren & {
    product: Product;
    axoFormOptions: Omit<
      ComponentProps<typeof AxoFormContextProvider<Data>>,
      // TODO: remove prop drilling from AxoFormContext and use ConfigService instead
      'children' | 'baseUrl' | 'marketCountry'
    >;
  };

export function FormGlobalProviders<Data extends Record<string, unknown>>({
  product,
  axoFormOptions,
  children,
}: FormGlobalProvidersProps<Data>) {
  const formRef = useRef<HTMLFormElement>(null);

  const { country } = ConfigService.get('global');
  const { apiUrl } = ConfigService.get('env').services;

  return (
    <TranslationProvider
      language={country.toLocaleLowerCase() as Locales}
      ns={product}
    >
      <DataAccessErrorFeedbackProvider>
        <AuthenticationContextProvider>
          <AxoFormContextProvider
            product={product}
            formRef={formRef}
            baseUrl={apiUrl}
            marketCountry={country as MarketCountryCode}
            {...axoFormOptions}
          >
            {children}
          </AxoFormContextProvider>
        </AuthenticationContextProvider>
      </DataAccessErrorFeedbackProvider>
    </TranslationProvider>
  );
}
